import React, { useState } from 'react';
import { X, ChevronRight, Sparkles } from 'lucide-react';
import { AppModal, AppButton } from '../ui';
import './WhatsNewModal.scss';

/**
 * WhatsNewModal — Samsung/Apple-style "What's New" carousel shown to
 * institute admins (superadmin / admin) after login.
 *
 * Content is delivered through images. The image fills the modal body.
 *
 * Props:
 *   release   — the WhatsNewRelease object (with slides[] image URLs resolved)
 *   onDone    — called when user clicks "Done" (permanent dismiss)
 *   onSkip    — called when user clicks X or "Skip for now" (session-only skip)
 */
const WhatsNewModal = ({ release, onDone, onSkip }) => {
  const [current, setCurrent] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const slides = release?.slides || [];
  const total = slides.length;
  const slide = slides[current];

  if (!release || !slide) return null;

  const handleSlideChange = (idx) => {
    setImgLoaded(false);
    setCurrent(idx);
  };

  if (!release || !slide) return null;

  const footerContent = (
    <div className="wn-slide__footer">
      <AppButton variant="outline" className="wn-slide__skip-btn" onClick={onSkip}>
        Skip for now
      </AppButton>

      {total > 1 ? (
        <div className="wn-slide__dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`wn-slide__dot${i === current ? ' wn-slide__dot--active' : ''}`}
              onClick={() => handleSlideChange(i)}
            />
          ))}
        </div>
      ) : <div />}

      {current < total - 1 ? (
        <AppButton className="wn-slide__next-btn" onClick={() => handleSlideChange(current + 1)}>
          Next <ChevronRight size={16} />
        </AppButton>
      ) : (
        <AppButton className="wn-slide__next-btn" onClick={onDone}>
          Done
        </AppButton>
      )}
    </div>
  );

  return (
    <AppModal show onHide={onSkip} size="xl" hideCloseButton className="wn-dialog" footer={footerContent}>
      <div className="wn-slide">
        {/* Close button */}
        <button className="wn-slide__close" onClick={onSkip} aria-label="Close">
          <X size={22} />
        </button>

        {/* Loading placeholder */}
        {!imgLoaded && (
          <div className="wn-slide__loading">
            <Sparkles size={32} className="wn-slide__loading-icon" />
            <span>Loading new features...</span>
          </div>
        )}

        {/* Image — fills the modal body edge-to-edge */}
        <div className={`wn-slide__image${imgLoaded ? '' : ' wn-slide__image--hidden'}`}>
          {slide.desktopImageUrl && (
            <img className="wn-slide__img-desktop" src={slide.desktopImageUrl} alt={slide.title || 'What\'s New'} onLoad={() => setImgLoaded(true)} />
          )}
          {slide.mobileImageUrl && (
            <img className="wn-slide__img-mobile" src={slide.mobileImageUrl} alt={slide.title || 'What\'s New'} onLoad={() => setImgLoaded(true)} />
          )}
        </div>
      </div>
    </AppModal>
  );
};

export default WhatsNewModal;
